'use client';

import {useEffect, useState} from 'react';
import {useLocale, useTranslations} from 'next-intl';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';

interface UserInfo {
  nom: string;
  role: string;
}

interface ProjetItem {
  id: number;
  titre_fr: string;
  categorie: string;
  statut: string;
  budget_objectif: number;
  montant_collecte: number;
  gouvernorat?: string;
  delegation?: string;
  pourcentage_avancement?: number;
}

interface DashboardStats {
  projets: { total: number; publies: number; en_attente: number };
  dons: { total: number; confirmes: number; montant_total: number; donateurs_uniques: number };
  campagnes_actives: number;
  couverture: { gouvernorats: number; delegations: number };
}

interface TimelineEvent {
  type: string;
  date: string;
  description: string;
  projet_id?: number;
  montant?: number;
}

// roleLabels are now resolved via useTranslations('dashboard') in the component

export default function DashboardPage() {
  const locale = useLocale();
  const t = useTranslations('dashboard');
  const tw = useTranslations('workflow');
  const [user, setUser] = useState<UserInfo & {gouvernorat_id?: number} | null>(null);
  const [projets, setProjets] = useState<ProjetItem[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [regionStats, setRegionStats] = useState<any>(null);

  useEffect(() => {
    const stored = localStorage.getItem('utss_user');
    if (stored) {
      const u = JSON.parse(stored);
      if (u.role === 'donateur') {
        window.location.href = `/${locale}/espace-donateur`;
        return;
      }
      setUser(u);
      const token = localStorage.getItem('utss_token');
      const headers: Record<string, string> = token ? {Authorization: `Bearer ${token}`} : {};
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      const staffRoles = ['contributeur_local', 'valideur_regional', 'valideur_central', 'admin_technique', 'admin_contenu', 'auditeur'];

      // Load projets — filter by gouvernorat for local/regional roles
      const govFilter = (u.role === 'contributeur_local' || u.role === 'valideur_regional') && u.gouvernorat_id
        ? `&gouvernorat_id=${u.gouvernorat_id}` : '';
      const statutFilter = staffRoles.includes(u.role) ? '&statut=all' : '';
      fetch(`${apiUrl}/api/v1/projets/?limit=50${govFilter}${statutFilter}`, {headers})
        .then(r => r.ok ? r.json() : [])
        .then(data => setProjets(data))
        .catch(() => {});

      // Load dashboard stats for ALL staff roles (not just admin)
      if (staffRoles.includes(u.role)) {
        fetch(`${apiUrl}/api/v1/dashboard/stats`, {headers})
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setStats(data); })
          .catch(() => {});

        fetch(`${apiUrl}/api/v1/dashboard/timeline`, {headers})
          .then(r => r.ok ? r.json() : [])
          .then(data => { if (Array.isArray(data)) setTimeline(data.slice(0, 10)); })
          .catch(() => {});

        // Regional stats for valideur_regional
        if (u.role === 'valideur_regional' && u.gouvernorat_id) {
          fetch(`${apiUrl}/api/v1/dashboard/region/${u.gouvernorat_id}`, {headers})
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (data) setRegionStats(data); })
            .catch(() => {});
        }
      }
    } else {
      window.location.href = `/${locale}/auth`;
    }
  }, [locale]);

  if (!user) return null;

  const label = t(`role_${user.role}`);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-primary">{t('title')}</h1>
          <p className="text-muted mt-1">
            {user.nom} — <Badge variant="primary">{label}</Badge>
          </p>
        </div>
        <button
          onClick={() => { localStorage.removeItem('utss_token'); localStorage.removeItem('utss_user'); window.location.href = `/${locale}/auth`; }}
          className="text-sm text-muted hover:text-secondary transition-colors"
        >
          {t('logout')}
        </button>
      </div>

      {/* === VALIDEUR REGIONAL (specific layout with region stats) === */}
      {user.role === 'valideur_regional' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard value={String(projets.length)} label={t('stat_projects')} sub={t('sub_my_region')} color="primary" />
            <StatCard value={String(projets.filter(p => p.statut === 'soumis' || p.statut === 'instruction').length)} label={t('stat_to_validate')} sub={t('sub_pending')} color="warning" />
            <StatCard value={stats ? `${(stats.dons.montant_total / 1000).toFixed(0)}K TND` : '—'} label={t('stat_donations')} sub={stats ? `${stats.dons.confirmes} ${t('sub_confirmed')}` : ''} color="success" />
            <StatCard value={regionStats ? String(regionStats.delegations_actives || 0) : '—'} label={t('stat_delegations')} sub={t('sub_my_region')} color="secondary" />
          </div>

          {regionStats && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <StatCard value={String(projets.filter(p => p.statut === 'en_cours').length)} label={t('stat_in_progress')} sub={t('sub_active')} color="success" />
              <StatCard value={String(projets.filter(p => p.statut === 'publie').length)} label={t('stat_collecting')} sub={t('sub_published')} color="primary" />
              <StatCard value={String(projets.filter(p => p.statut === 'cloture').length)} label={t('stat_completed')} sub={t('sub_closed')} color="muted" />
            </div>
          )}

          <div className="flex gap-3 mb-6">
            <a href={`/${locale}/projets`} style={{color: '#fff', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors no-underline">
              {t('projects_to_validate')}
            </a>
            <a href={`/${locale}/structures`} style={{color: '#1B5E20', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-primary text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/5 transition-colors no-underline">
              {t('regional_map')}
            </a>
          </div>

          <Card>
            <h2 className="text-lg font-semibold text-primary mb-4">{t('region_projects', {count: projets.length})}</h2>
            {projets.length === 0 ? (
              <p className="text-muted text-sm">{t('no_projects_region')}</p>
            ) : (
              <div className="divide-y divide-border">
                {projets.slice(0, 20).map(p => {
                  const pct = p.budget_objectif > 0 ? Math.round((p.montant_collecte / p.budget_objectif) * 100) : 0;
                  return (
                    <a key={p.id} href={`/${locale}/projets/${p.id}`} className="flex items-center gap-4 py-3 hover:bg-surface/50 transition-colors rounded px-2 -mx-2" style={{textDecorationColor: 'transparent'}}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">{p.titre_fr}</span>
                          <StatutBadge statut={p.statut} label={tw(`status_${p.statut}`)} />
                        </div>
                        <div className="text-xs text-muted mt-0.5">{p.categorie} — {p.delegation}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold" style={{color: pct >= 100 ? '#2E7D32' : pct >= 50 ? '#1B5E20' : '#F57C00'}}>{pct}%</div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </Card>

          {timeline.length > 0 && (
            <Card className="mt-6">
              <h2 className="text-lg font-semibold text-primary mb-4">{t('recent_activity')}</h2>
              <div className="space-y-3">
                {timeline.map((ev, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${ev.type === 'don' ? 'bg-success' : ev.type === 'transition' ? 'bg-primary' : 'bg-warning'}`} />
                    <div className="flex-1 min-w-0"><span className="text-foreground">{ev.description}</span></div>
                    <span className="text-xs text-muted shrink-0">{new Date(ev.date).toLocaleDateString('fr-TN', {day: '2-digit', month: 'short'})}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* === AGENT LOCAL === */}
      {user.role === 'contributeur_local' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard value={String(projets.length)} label={t('stat_projects')} sub={t('sub_my_region')} color="primary" />
            <StatCard value={String(projets.filter(p => p.statut === 'en_cours').length)} label={t('stat_in_progress')} sub={t('sub_active')} color="success" />
            <StatCard value={String(projets.filter(p => p.statut === 'publie').length)} label={t('stat_collecting')} sub={t('sub_published')} color="warning" />
            <StatCard value={projets.length > 0 ? Math.round(projets.reduce((s, p) => s + (p.pourcentage_avancement || 0), 0) / projets.length) + '%' : '0%'} label={t('stat_avg_progress')} sub={t('sub_all_projects')} color="secondary" />
          </div>

          <div className="flex gap-3 mb-6">
            <a href={`/${locale}/projets/nouveau`} style={{color: '#fff', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors no-underline">
              + {t('create_project')}
            </a>
            <a href={`/${locale}/structures`} style={{color: '#1B5E20', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-primary text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/5 transition-colors no-underline">
              {t('projects_map')}
            </a>
          </div>

          <Card>
            <h2 className="text-lg font-semibold text-primary mb-4">{t('my_projects', {count: projets.length})}</h2>
            {projets.length === 0 ? (
              <p className="text-muted text-sm">{t('no_projects_yet')}</p>
            ) : (
              <div className="divide-y divide-border">
                {projets.slice(0, 20).map(p => {
                  const pct = p.budget_objectif > 0 ? Math.round((p.montant_collecte / p.budget_objectif) * 100) : 0;
                  return (
                    <a key={p.id} href={`/${locale}/projets/${p.id}`} className="flex items-center gap-4 py-3 hover:bg-surface/50 transition-colors rounded px-2 -mx-2" style={{textDecorationColor: 'transparent'}}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">{p.titre_fr}</span>
                          <StatutBadge statut={p.statut} label={tw(`status_${p.statut}`)} />
                        </div>
                        <div className="text-xs text-muted mt-0.5">{p.categorie} — {p.gouvernorat} &gt; {p.delegation}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold" style={{color: pct >= 100 ? '#2E7D32' : pct >= 50 ? '#1B5E20' : '#F57C00'}}>{pct}%</div>
                        <div className="text-[10px] text-muted">{p.montant_collecte?.toLocaleString()} / {p.budget_objectif?.toLocaleString()} TND</div>
                      </div>
                      <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </a>
                  );
                })}
              </div>
            )}
            {projets.length > 20 && (
              <a href={`/${locale}/projets`} className="block text-center text-sm text-primary mt-4 hover:underline">{t('see_all')}</a>
            )}
          </Card>
        </>
      )}

      {/* === TOUS LES AUTRES ROLES (DG, valideur central, admin, auditeur) === */}
      {user.role !== 'contributeur_local' && user.role !== 'valideur_regional' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard value={stats ? String(stats.projets.total) : String(projets.length)} label={t('stat_projects')} sub={user.role === 'valideur_regional' ? t('sub_my_region') : t('sub_national')} color="primary" />
            <StatCard value={String(projets.filter(p => p.statut === 'soumis' || p.statut === 'instruction').length)} label={t('stat_to_validate')} sub={t('sub_pending')} color="warning" />
            <StatCard value={stats ? `${(stats.dons.montant_total / 1000).toFixed(0)}K TND` : String(projets.filter(p => p.statut === 'en_cours').length)} label={stats ? t('stat_donations') : t('stat_in_progress')} sub={stats ? `${stats.dons.confirmes} ${t('sub_confirmed')}` : t('sub_active')} color="success" />
            <StatCard value={stats ? String(stats.dons.donateurs_uniques) : String(projets.filter(p => p.statut === 'publie').length)} label={stats ? t('stat_donors') : t('stat_collecting')} sub={stats ? t('sub_unique_donors') : t('sub_published')} color="secondary" />
          </div>

          {/* Stats supplementaires pour DG/admin */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard value={String(stats.projets.publies)} label={t('stat_collecting')} sub={t('sub_published')} color="primary" />
              <StatCard value={String(projets.filter(p => p.statut === 'en_cours').length)} label={t('stat_in_progress')} sub={t('sub_active')} color="success" />
              <StatCard value={String(stats.campagnes_actives)} label={t('stat_campaigns')} sub={t('sub_active')} color="warning" />
              <StatCard value={`${stats.couverture.gouvernorats}/24`} label={t('stat_coverage')} sub={`${stats.couverture.delegations} ${t('sub_delegations')}`} color="secondary" />
            </div>
          )}

          <div className="flex gap-3 mb-6">
            <a href={`/${locale}/projets`} style={{color: '#fff', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors no-underline">
              {t('all_projects')}
            </a>
            <a href={`/${locale}/structures`} style={{color: '#1B5E20', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-primary text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/5 transition-colors no-underline">
              {t('national_map')}
            </a>
            {(user.role === 'admin_contenu' || user.role === 'admin_technique') && (
              <a href={`/${locale}/admin/contenu`} style={{color: '#666', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-border text-sm font-medium px-4 py-2 rounded-lg hover:bg-surface transition-colors no-underline">
                {t('manage_content')}
              </a>
            )}
            {(user.role === 'admin_technique' || user.role === 'auditeur') && (
              <a href={`/${locale}/audit`} style={{color: '#666', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-border text-sm font-medium px-4 py-2 rounded-lg hover:bg-surface transition-colors no-underline">
                {t('audit_log')}
              </a>
            )}
            {user.role === 'admin_technique' && (
              <a href={`/${locale}/admin/utilisateurs`} style={{color: '#666', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-border text-sm font-medium px-4 py-2 rounded-lg hover:bg-surface transition-colors no-underline">
                {t('users')}
              </a>
            )}
            {user.role === 'admin_technique' && (
              <a href={`/${locale}/admin/import`} style={{color: '#666', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-border text-sm font-medium px-4 py-2 rounded-lg hover:bg-surface transition-colors no-underline">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                Import en Masse
              </a>
            )}
            {user.role === 'admin_technique' && (
              <a href="http://141.227.150.233:8091/docs" target="_blank" style={{color: '#666', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-border text-sm font-medium px-4 py-2 rounded-lg hover:bg-surface transition-colors no-underline">
                API Swagger
              </a>
            )}
          </div>

          <Card>
            <h2 className="text-lg font-semibold text-primary mb-4">{t('projects_count', {count: projets.length})}</h2>
            {projets.length === 0 ? (
              <p className="text-muted text-sm">{t('no_projects_visible')}</p>
            ) : (
              <div className="divide-y divide-border">
                {projets.slice(0, 20).map(p => {
                  const pct = p.budget_objectif > 0 ? Math.round((p.montant_collecte / p.budget_objectif) * 100) : 0;
                  return (
                    <a key={p.id} href={`/${locale}/projets/${p.id}`} className="flex items-center gap-4 py-3 hover:bg-surface/50 transition-colors rounded px-2 -mx-2" style={{textDecorationColor: 'transparent'}}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">{p.titre_fr}</span>
                          <StatutBadge statut={p.statut} label={tw(`status_${p.statut}`)} />
                        </div>
                        <div className="text-xs text-muted mt-0.5">{p.categorie} — {p.gouvernorat} &gt; {p.delegation}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-bold" style={{color: pct >= 100 ? '#2E7D32' : pct >= 50 ? '#1B5E20' : '#F57C00'}}>{pct}%</div>
                        <div className="text-[10px] text-muted">{p.montant_collecte?.toLocaleString()} / {p.budget_objectif?.toLocaleString()} TND</div>
                      </div>
                      <svg className="w-4 h-4 text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </a>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Timeline des evenements recents */}
          {timeline.length > 0 && (
            <Card className="mt-6">
              <h2 className="text-lg font-semibold text-primary mb-4">{t('recent_activity')}</h2>
              <div className="space-y-3">
                {timeline.map((ev, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                      ev.type === 'don' ? 'bg-success' :
                      ev.type === 'transition' ? 'bg-primary' :
                      'bg-warning'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <span className="text-foreground">{ev.description}</span>
                      {ev.montant && <span className="text-success font-medium ml-1">{ev.montant.toLocaleString()} TND</span>}
                    </div>
                    <span className="text-xs text-muted shrink-0">
                      {new Date(ev.date).toLocaleDateString('fr-TN', {day: '2-digit', month: 'short'})}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/* === Sub-components === */

function StatCard({value, label, sub, color}: {value: string; label: string; sub: string; color: string}) {
  const colorClass = color === 'primary' ? 'text-primary' : color === 'secondary' ? 'text-secondary' : color === 'success' ? 'text-success' : 'text-warning';
  return (
    <Card>
      <div className="text-center">
        <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
        <div className="text-muted text-sm">{label}</div>
        <div className="text-xs text-muted mt-1">{sub}</div>
      </div>
    </Card>
  );
}

function ActionLink({href, icon, label, target}: {href: string; icon: string; label: string; target?: string}) {
  return (
    <a href={href} target={target} className="p-4 rounded-lg bg-surface hover:bg-primary/5 transition-colors text-center block">
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-sm font-medium">{label}</div>
    </a>
  );
}

function Step({n, label}: {n: number; label: string}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center shrink-0 font-bold">{n}</div>
      <span>{label}</span>
    </div>
  );
}

const statutStyles: Record<string, {bg: string; text: string}> = {
  publie: {bg: 'bg-blue-100', text: 'text-blue-700'},
  en_cours: {bg: 'bg-green-100', text: 'text-green-700'},
  en_collecte: {bg: 'bg-orange-100', text: 'text-orange-700'},
  finance: {bg: 'bg-green-100', text: 'text-green-700'},
  termine: {bg: 'bg-gray-100', text: 'text-gray-600'},
  urgence: {bg: 'bg-red-100', text: 'text-red-700'},
  brouillon: {bg: 'bg-gray-100', text: 'text-gray-500'},
  soumis: {bg: 'bg-yellow-100', text: 'text-yellow-700'},
  valide: {bg: 'bg-blue-100', text: 'text-blue-700'},
};

function StatutBadge({statut, label}: {statut: string; label: string}) {
  const s = statutStyles[statut] || {bg: 'bg-gray-100', text: 'text-gray-600'};
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>{label}</span>;
}
